import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Loader2, Bell, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grouped presentation of the pref keys returned by the backend.
// `hostOnly` groups render only for admin / sub-admin roles.
const GROUPS = [
  {
    title: 'Live class alerts (student)',
    items: [
      { key: 'class_rescheduled_email', label: 'Class rescheduled — email', description: 'Fires when a class you can attend is rescheduled.' },
      { key: 'class_cancelled_email', label: 'Class cancelled — email', description: 'Fires when a class you can attend is cancelled.' },
      { key: 'class_reminder_1h_email', label: 'Reminder 1 hour before class starts', description: 'Optional heads-up so you don\u2019t miss the class.' },
      { key: 'class_starting_15m_in_app', label: 'In-app: join window opens (15m before)', description: '' },
      { key: 'class_live_now_in_app', label: 'In-app: class is live now', description: '' },
    ],
  },
  {
    title: 'Digests',
    items: [
      { key: 'new_classes_daily_digest_email', label: 'Daily digest — new classes in your grade + MMD', description: 'One email at 6 AM IST, only if there are new classes.' },
      { key: 'class_recap_email', label: 'Post-class recap email (summary + recording + Q&A)', description: 'Sent when the class archive is published.' },
      { key: 'monthly_community_ask_email', label: 'Monthly community ask', description: 'End-of-month soft nudge to share exam questions you remember. Never more than once a month.' },
    ],
  },
  {
    title: 'Host alerts (admin / sub-admin)',
    hostOnly: true,
    items: [
      { key: 'host_assigned_email', label: 'You\u2019ve been assigned as host', description: '' },
      { key: 'host_class_reminder_1h_email', label: 'Reminder 1 hour before your class starts', description: '' },
      { key: 'host_qna_daily_digest_email', label: 'Daily digest — new Q&A on your classes', description: 'One rollup email per day, no per-question emails.' },
    ],
  },
];

const NotificationPreferences = () => {
  const { user } = useAuth();
  const isHostRole = user?.role === 'admin' || user?.role === 'sub_admin';
  const visibleGroups = GROUPS.filter((g) => !g.hostOnly || isHostRole);
  const [prefs, setPrefs] = useState({});
  const [mandatoryKeys, setMandatoryKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/preferences`);
        setPrefs(data.prefs || {});
        setMandatoryKeys(data.mandatory_keys || []);
      } catch (e) {
        toast.error('Failed to load notification preferences');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onToggle = (key, value) => {
    if (mandatoryKeys.includes(key)) return; // Locked — always on
    setPrefs((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, { prefs });
      toast.success('Notification preferences saved');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="notification-preferences-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-[#0A7EA4]">
          <Bell className="w-5 h-5" />
          Notification preferences
        </CardTitle>
        <CardDescription>
          Choose which emails and in-app alerts you receive. A couple of critical safety alerts (rescheduled / cancelled class) are always on.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-6">
            {visibleGroups.map((group) => (
              <div key={group.title}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{group.title}</p>
                <div className="space-y-2">
                  {group.items.map((item) => {
                    const locked = mandatoryKeys.includes(item.key);
                    const value = !!prefs[item.key];
                    return (
                      <div
                        key={item.key}
                        className={`flex items-start justify-between gap-3 py-2 px-3 rounded-md border ${locked ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-100'}`}
                        data-testid={`pref-row-${item.key}`}
                      >
                        <div className="flex-1 min-w-0">
                          <Label className="text-sm font-medium text-gray-800 flex items-center gap-1.5 cursor-pointer">
                            {item.label}
                            {locked && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-500 bg-gray-200 rounded px-1.5 py-0.5">
                                <Lock className="w-2.5 h-2.5" /> Always on
                              </span>
                            )}
                          </Label>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                          )}
                        </div>
                        <Switch
                          checked={value}
                          onCheckedChange={(v) => onToggle(item.key, v)}
                          disabled={locked || saving}
                          data-testid={`pref-toggle-${item.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <Button onClick={save} disabled={!dirty || saving} data-testid="pref-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save changes
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationPreferences;
